import { useState, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import ConstellationBackground from "@/components/ConstellationBackground";
import HeroSection from "@/components/HeroSection";

const LoadingScreenWrapper = lazy(() => import("@/components/LoadingScreenWrapper"));

const SectionDivider = lazy(() => import("@/components/SectionDivider"));
const AnimatedSection = lazy(() => import("@/components/AnimatedSection"));

// Lazy-load below-the-fold sections to reduce initial JS
const AboutSection = lazy(() => import("@/components/AboutSection"));
const StatsSection = lazy(() => import("@/components/StatsSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const SkillsSection = lazy(() => import("@/components/SkillsSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const FaqSection = lazy(() => import("@/components/FaqSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const FooterSection = lazy(() => import("@/components/FooterSection"));
const BackToTop = lazy(() => import("@/components/BackToTop"));

const faqs = [
  {
    q: "Where can I buy official AI tool plans in Pakistan?",
    a: "You can buy cheap, official AI tool subscriptions (ChatGPT Plus, Claude Pro, ElevenLabs, Google AI Pro) in Pakistan from Evil Markhors Services. We upgrade your own personal email address privately without asking for passwords or Gmail logins, ensuring 100% data safety.",
  },
  {
    q: "Can I get a verified PayPal account in Pakistan?",
    a: "Yes. Evil Markhors Services offers a fully verified and secured PayPal account setup (Business or Personal) on your own email address from Pakistan within 3 to 4 hours, featuring a lifetime guarantee and support.",
  },
  {
    q: "What is included in the SMM Panel Package?",
    a: "Our Pak SMM Panel package includes a complete script, 1 year free domain registration, 1 year high-speed web hosting, 20+ custom developer themes, and pre-integrated EasyPaisa, JazzCash, and SadaPay local payment gateways.",
  },
  {
    q: "How to get GoDaddy domains and hosting at 50% discount in Pakistan?",
    a: "You can buy or renew any GoDaddy services (domains, hosting, VPS, Dedicated servers, SSL, email) at exactly 50% off GoDaddy's official pricing through Evil Markhors Services. We process registrations and renewals securely directly inside your own existing account via Delegate Access (no passwords required) or set up a brand new account for you.",
  },
  {
    q: "Where to buy Google Antigravity plan or Antigravity coding assistant in Pakistan?",
    a: "You can buy the Google Antigravity plan / Antigravity coding assistant by ordering the Google AI Pro 18 Months subscription from Evil Markhors for PKR 2,199 - 2,999. It includes full private activation on your personal Gmail, with unlocked high rate limits for Google Antigravity and Jules coding agent, 5TB cloud storage, and a 1-Year replacement warranty. Password or Gmail login credentials are NOT required.",
  },
  {
    q: "What payment options do you support for local clients in Pakistan?",
    a: "We support local, hassle-free payment methods including EasyPaisa, JazzCash, SadaPay, bank transfers, and international payments via cryptocurrency (Binance Pay) to avoid international credit card declines.",
  },
  {
    q: "How long does SMM Panel deployment and AI subscription activation take?",
    a: "SMM Panel business setups are completed and deployed within 24 to 48 hours. Premium AI tools subscriptions (ChatGPT Plus, Claude Pro, Gemini AI Pro) are activated within 15 to 30 minutes after payment verification.",
  },
  {
    q: "Can I buy cheap, official TradingView Premium accounts in Pakistan?",
    a: "Yes. You can buy official TradingView subscriptions (Essential Rs 2,000, Plus Rs 2,500, Premium Rs 3,500) from Evil Markhors. We activate them securely on your email without requiring passwords, backed by a 30-day replacement warranty.",
  },
  {
    q: "Where can I buy cheap, official CapCut Pro Premium subscriptions in Pakistan?",
    a: "You can buy official CapCut Pro Premium subscriptions (1 Month Rs 1,199, 6 Months Rs 4,999) from Evil Markhors. Sourced legally and activated instantly via WhatsApp, they feature premium effects unlocked with local payment support.",
  },
];

const Index = () => {
  const [loading, setLoading] = useState(true);

  return (
    <div className="min-h-screen bg-background relative">
      <Helmet>
        <title>Evil Markhors Services | Cybersecurity, SMM & AI Store PK</title>
        <meta name="description" content="Pakistan's cheapest SMM panel, trusted AI tool store, and expert cybersecurity agency. Get verified logins, hosting, and pen testing. 100% secure." />
        <meta name="keywords" content="Evil Markhors Services, cheapest AI store Pakistan, buy AI subscriptions Pakistan, SMM Panel Pakistan, Pak SMM Panel, best SMM panel Pakistan, cheapest SMM panel, Penetration Testing Pakistan, buy cheap ChatGPT Plus Pakistan, cheap Claude Pro Pakistan, buy ElevenLabs Pakistan, trusted AI seller Pakistan, digital forensics Pakistan, security consulting, domain hosting Pakistan" />
        <link rel="canonical" href="https://evilmarkhors.tech/" />
        <link rel="alternate" type="text/plain" href="https://evilmarkhors.tech/llms.txt" title="LLM Ingestion Feed" />
        <link rel="alternate" type="text/plain" href="https://evilmarkhors.tech/llms-full.txt" title="LLM Technical Specs" />
        <link rel="alternate" type="application/atom+xml" title="Evil Markhors Services Feed" href="https://evilmarkhors.tech/atom.xml" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Evil Markhors Services Team" />
        <meta name="date" content="2026-06-23" />
        <meta name="last-modified" content="2026-07-29" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="ur_PK" />
        <meta property="og:title" content="Evil Markhors Services — SMM Panel Pakistan & Cybersecurity Experts" />
        <meta property="og:description" content="Pakistan's trusted ethical hackers and digital reseller. Cheapest SMM Panel, premium AI tools (ChatGPT Plus, Claude Pro, Google AI Pro), Hostinger hosting, and Penetration Testing." />
        <meta property="og:url" content="https://evilmarkhors.tech/" />
        <meta property="og:image" content="https://evilmarkhors.tech/images/evil-markhors-logo.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Evil Markhors Services — SMM Panel Pakistan & Cybersecurity Experts" />
        <meta name="twitter:description" content="Pakistan's trusted ethical hackers and digital reseller. Cheapest SMM Panel, AI tools, Hostinger hosting, and Penetration Testing." />
        <meta name="twitter:image" content="https://evilmarkhors.tech/images/evil-markhors-logo.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SpeakableSpecification",
                "@id": "https://evilmarkhors.tech/#speakable",
                "cssSelector": ["#hero-title", "#hero-summary"]
              },
              {
                "@type": "Organization",
                "@id": "https://evilmarkhors.tech/#organization",
                "name": "Evil Markhors Services",
                "url": "https://evilmarkhors.tech",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://evilmarkhors.tech/images/evil-markhors-logo.webp",
                  "width": "512",
                  "height": "512"
                },
                "contactPoint": [
                  {
                    "@type": "ContactPoint",
                    "telephone": "+27621692852",
                    "contactType": "customer service",
                    "areaServed": "PK",
                    "availableLanguage": ["English", "Urdu"]
                  },
                  {
                    "@type": "ContactPoint",
                    "telephone": "+27643305204",
                    "contactType": "technical support",
                    "areaServed": "PK",
                    "availableLanguage": ["English", "Urdu"]
                  }
                ],
                "founder": {
                  "@type": "Person",
                  "name": "Muhammad Abu Turab"
                },
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Evil Markhors Official Digital & Cybersecurity Services",
                  "itemListElement": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "GoDaddy 50% Off Domains, Web Hosting & VPS Reseller",
                        "url": "https://evilmarkhors.tech/godaddy"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Verified PayPal Account Setup in Pakistan",
                        "url": "https://evilmarkhors.tech/paypal"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Google AI Pro 18 Months with 5TB Storage & Antigravity",
                        "url": "https://evilmarkhors.tech/google-ai-pro"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Google Veo 3.1 Bunny Flow Video AI Subscriptions",
                        "url": "https://evilmarkhors.tech/veo-bunny-flow"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Product",
                        "name": "Wifi Hack Device Educational Cybersecurity Trainer",
                        "url": "https://evilmarkhors.tech/wifi-hack-device"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Pak SMM Panel — Social Media Marketing Services",
                        "url": "https://evilmarkhors.tech/smm-panel"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Course",
                        "name": "Evil Markhors Cybersecurity & AI Training Academy",
                        "url": "https://evilmarkhors.tech/academy"
                      }
                    }
                  ]
                },
                "sameAs": [
                  "https://github.com/evilmarkhors",
                  "https://evilmarkhors.tech/services",
                  "https://evilmarkhors.tech/atom.xml"
                ],
                "knowsAbout": [
                  {
                    "@type": "Thing",
                    "name": "Cybersecurity",
                    "sameAs": "https://www.wikidata.org/wiki/Q350352"
                  },
                  {
                    "@type": "Thing",
                    "name": "Financial Technology",
                    "sameAs": "https://www.wikidata.org/wiki/Q19361668"
                  },
                  {
                    "@type": "Thing",
                    "name": "Web Hosting",
                    "sameAs": "https://www.wikidata.org/wiki/Q271813"
                  }
                ]
              },
              {
                "@type": "ProfessionalService",
                "@id": "https://evilmarkhors.tech/#service-business",
                "name": "Evil Markhors Cybersecurity & Reseller Services",
                "image": "https://evilmarkhors.tech/images/evil-markhors-logo.webp",
                "telephone": "+27621692852",
                "url": "https://evilmarkhors.tech",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Karachi",
                  "addressRegion": "Sindh",
                  "addressCountry": "PK"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": "24.8607",
                  "longitude": "67.0011"
                },
                "openingHoursSpecification": {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday"
                  ],
                  "opens": "00:00",
                  "closes": "23:59"
                },
                "priceRange": "$$"
              },
              {
                "@type": "WebSite",
                "@id": "https://evilmarkhors.tech/#website",
                "url": "https://evilmarkhors.tech/",
                "name": "Evil Markhors Services",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://evilmarkhors.tech/services?q={search_term_string}"
                  },
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "FAQPage",
                "@id": "https://evilmarkhors.tech/#faq",
                "mainEntity": faqs.map((faq) => ({
                  "@type": "Question",
                  "name": faq.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.a
                  }
                }))
              },
              {
                "@type": "TechArticle",
                "@id": "https://evilmarkhors.tech/#article",
                "isPartOf": { "@id": "https://evilmarkhors.tech/#website" },
                "headline": "Evil Markhors Services — SMM Panel Pakistan & Premium AI Subscriptions Store",
                "description": "Pakistan's premier cybersecurity agency, best SMM panel provider, and cheapest trusted AI tools subscription store.",
                "inLanguage": "en",
                "about": [
                  {
                    "@type": "Thing",
                    "name": "Cybersecurity",
                    "sameAs": "https://www.wikidata.org/wiki/Q350352"
                  },
                  {
                    "@type": "Thing",
                    "name": "Social Media Marketing",
                    "sameAs": "https://www.wikidata.org/wiki/Q627272"
                  },
                  {
                    "@type": "Thing",
                    "name": "Artificial Intelligence",
                    "sameAs": "https://www.wikidata.org/wiki/Q11660"
                  }
                ],
                "mainEntityOfPage": "https://evilmarkhors.tech/",
                "datePublished": "2026-06-23T09:00:00+05:00",
                "dateModified": "2026-07-03T10:00:00+05:00",
                "author": {
                  "@type": "Person",
                  "name": "RAY",
                  "jobTitle": "Lead Security Architect at Evil Markhors",
                  "sameAs": "https://evilmarkhors.tech"
                },
                "publisher": { "@id": "https://evilmarkhors.tech/#organization" }
              }
            ]
          })}
        </script>
      </Helmet>

      {loading && (
        <Suspense fallback={null}>
          <LoadingScreenWrapper onComplete={() => setLoading(false)} />
        </Suspense>
      )}
      <ConstellationBackground />
      <Navbar />
      <main>
        <h1 id="hero-title" className="sr-only">Evil Markhors Services — SMM Panel Pakistan, Hostinger Web Hosting & Premium AI Subscriptions Store</h1>
        <h2 className="sr-only">Cybersecurity Audits, Pak SMM Reseller Panels, and Discounted ChatGPT, Claude & Gemini Upgrades</h2>
        <h2 className="sr-only">Premium AI Subscriptions and Web Hosting Services in Pakistan</h2>
        <h2 className="sr-only">Frequently Asked Questions &amp; Verified Support Details</h2>
        <div className="sr-only">
          <h2>Overview of Services</h2>
          <ul>
            <li>Cybersecurity consulting &amp; penetration testing</li>
            <li>Pak SMM Panel setups &amp; SMM Reseller scripts</li>
            <li>Hostinger business web hosting &amp; domains reseller</li>
            <li>Cheapest ChatGPT Plus, Claude Pro &amp; Google AI Pro subscriptions</li>
          </ul>
          <h2>Accepted Payment Methods in Pakistan</h2>
          <ul>
            <li>EasyPaisa (Mobile Wallet)</li>
            <li>JazzCash (Mobile Wallet)</li>
            <li>SadaPay / Nayapay (Digital Cards)</li>
            <li>Bank Transfer (All Pakistani Banks)</li>
            <li>Binance Pay (Cryptocurrency / USDT)</li>
          </ul>
          <h2>Service Plan Comparisons</h2>
          <table className="w-px h-px overflow-hidden table-fixed">
            <thead>
              <tr>
                <th>Service Name</th>
                <th>Standard Delivery</th>
                <th>Warranty / Guarantee Coverage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>ChatGPT Plus Activation</td>
                <td>15 - 30 Minutes</td>
                <td>25 Days Complete Replacement</td>
              </tr>
              <tr>
                <td>Google AI Pro &amp; Antigravity (18 Months)</td>
                <td>15 - 30 Minutes</td>
                <td>1 Year Full Technical Warranty</td>
              </tr>
              <tr>
                <td>Verified PayPal Setup</td>
                <td>3 - 4 Hours</td>
                <td>Lifetime Account Support</td>
              </tr>
              <tr>
                <td>SMM Panel Business Deployment</td>
                <td>24 - 48 Hours</td>
                <td>1 Year Server &amp; Domain Maintenance</td>
              </tr>
              <tr>
                <td>CapCut Pro Subscription</td>
                <td>15 - 30 Minutes</td>
                <td>25 Days (1 Month) / 5 Months (6 Months)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <HeroSection />
        <Suspense fallback={null}>
          <SectionDivider />
          <AnimatedSection direction="fade-up">
            <AboutSection />
          </AnimatedSection>
          <SectionDivider />
          <AnimatedSection direction="scale-in">
            <StatsSection />
          </AnimatedSection>
          <SectionDivider />
          <AnimatedSection direction="fade-left">
            <ServicesSection />
          </AnimatedSection>
          <SectionDivider />
          <AnimatedSection direction="fade-right">
            <SkillsSection />
          </AnimatedSection>
          <SectionDivider />
          <AnimatedSection direction="fade-right">
            <TestimonialsSection />
          </AnimatedSection>
          <SectionDivider />
          <AnimatedSection direction="fade-up">
            <FaqSection />
          </AnimatedSection>
          <SectionDivider />
          <AnimatedSection direction="fade-up">
            <ContactSection />
          </AnimatedSection>
          <AnimatedSection direction="fade-up" delay={0.1}>
            <FooterSection />
          </AnimatedSection>
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <BackToTop />
      </Suspense>
    </div>
  );
};

export default Index;
